import { Avatar, Box, Button, CircularProgress, IconButton, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useSearchTerm } from "./hooks/useSearchTerm";
import TimeTravelGraph from "./components/Graph/TimeTravelGraph";
import TimeTravelTrend from "./components/ForecastComponents/TimeTravelTrend";
import ForecastParameters from "./components/ForecastAnalytics/ForecastParameters";
import { useSearchVolume } from "./hooks/useSearchTrends";
import { useState } from "react";
import { Search } from "@mui/icons-material";
import SearchTermRanks from "./components/SearchTerms/SearchTermRanks";
import TimeTravelSeasonality from "./components/ForecastComponents/TimeTravelSeasonality";
import ForecastGrade from "./components/ForecastAnalytics/ForecastGrade";
import { useImpersonation } from "@shared/hooks/useImpersonation";
import PageLayout from "@shared/shell/Layout/PageLayout";
import SearchTermSpotlight from "./components/SearchTerms/SearchTermSpotlight";
import { ForecastSourceType } from "@domain/forecast/models/Forecast";
import { Icon, IconSize } from "@shared/icons";


const TimeTravelSearch: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();

    const { searchTerm: searchTermParam } = useParams<{ searchTerm: string }>();
    const { searchTerm, isLoading: isSearchTermLoading } = useSearchTerm(searchTermParam);
    const { searchVolume } = useSearchVolume(searchTermParam || '');

    const [showSpotlight, setShowSpotlight] = useState(false);

    const { canImpersonate } = useImpersonation();

    const handleSearchTermClick = (term: string) => {
        setShowSpotlight(false);
        navigate(`/timetravel/search/${term}`);
    };

    const [showParameters, setShowParameters] = useState(false);

    return (
        <PageLayout
            title="Time Travel Search"
        >
            <SearchTermSpotlight
                open={showSpotlight}
                onClose={() => setShowSpotlight(false)}
                onSelect={handleSearchTermClick}
            />


            <Stack direction="column" spacing={6}>


                <Stack direction="row" spacing={2} alignItems="center" borderBottom={1} py={2} borderColor={theme.palette.divider}
                    position="sticky" top={0} bgcolor="white" zIndex={1}>
                    <Stack flex={1} direction="row" alignItems="center" gap={2}>

                        <IconButton onClick={() => setShowSpotlight(true)}>
                            <Icon color={theme.palette.text.primary} icon="Search01" size={IconSize.LARGE}></Icon>
                        </IconButton>

                        <Typography variant="h6"
                            color="text.primary"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                display: '-webkit-box',
                            }}
                        >
                            {searchTermParam}
                        </Typography>

                        <ForecastGrade grade={searchTerm?.forecastGrade} />
                    </Stack>

                    {searchTerm?.connector && (
                        <Avatar variant="rounded" src={searchTerm.connector.image} sx={{ border: 1, width: 56, height: 32, borderColor: 'divider' }} />
                    )}
                </Stack>

                {!searchTerm && (
                    <Stack alignItems="center">
                        <CircularProgress></CircularProgress>
                    </Stack>
                )}


                {searchTerm && (
                    <>
                        <TimeTravelGraph
                            sourceType={ForecastSourceType.SEARCH}
                            source={searchTerm.searchTerm}
                            connector={searchTerm.connectorType}
                            historicalData={searchVolume?.map(volume => ({
                                week: volume.week,
                                year: volume.year,
                                value: volume.volume,
                                anomaly: false
                            }))}
                        />

                        <Stack direction="row" spacing={2}>
                            <TimeTravelTrend
                                sourceType={ForecastSourceType.SEARCH}
                                source={searchTerm.searchTerm}
                                connector={searchTerm.connectorType}
                            />

                            <TimeTravelSeasonality
                                sourceType={ForecastSourceType.SEARCH}
                                source={searchTerm.searchTerm}
                                connector={searchTerm.connectorType}
                            />
                        </Stack>

                        {canImpersonate && (
                            <Button size="small" onClick={() => setShowParameters(!showParameters)}>
                                {showParameters ? 'Hide Parameters' : 'Show Parameters'}
                            </Button>
                        )}

                    </>
                )}

                {searchTerm?.connectorType && showParameters && (
                    <ForecastParameters sourceType={ForecastSourceType.SEARCH} source={searchTerm.searchTerm} connector={searchTerm.connectorType} />
                )}


            </Stack>

        </PageLayout>
    );
};

export default TimeTravelSearch;
